import { AddressSuggestion } from '@@types'
import { gql, useLazyQuery, useQuery } from '@apollo/client'

// region Query Types
export interface GetAddressSuggestionsQuery {
  getAddressSuggestions: {
    suggestions: AddressSuggestion[]
  }
}

export interface GetAddressSuggestionsVariables {
  address: string
}
// endregion

// region GraphQL Query
const GQL_QUERY_GET_ADDRESS_SUGGESTIONS = gql`
  query getAddressSuggestions($address: String!) {
    getAddressSuggestions(address: $address) {
      suggestions {
        value
        unrestricted_value
        data {
          city
          house
          house_type
          block
          block_type
          street_with_type
          settlement
          latitude: geo_lat
          longitude: geo_lon
          qc_geo
        }
      }
    }
  }
`
// endregion

/**
 * Hook for fetching address suggestions by address text
 */
export const useFetchAddressSuggestions = (address: string, skip = false) => {
  const { data, loading, error, refetch } = useQuery<GetAddressSuggestionsQuery, GetAddressSuggestionsVariables>(
    GQL_QUERY_GET_ADDRESS_SUGGESTIONS,
    {
      variables: { address },
      skip: skip || !address,
      fetchPolicy: 'network-only',
    }
  )

  return {
    suggestions: data?.getAddressSuggestions?.suggestions ?? [],
    loading,
    error,
    refetch,
  }
}

/**
 * Lazy hook for programmatically fetching address suggestions
 */
export const useLazyFetchAddressSuggestions = () => {
  const [ fetchSuggestions, { data, loading, error } ] = useLazyQuery<
    GetAddressSuggestionsQuery,
    GetAddressSuggestionsVariables
  >(GQL_QUERY_GET_ADDRESS_SUGGESTIONS, {
    fetchPolicy: 'network-only',
  })

  return {
    fetchSuggestions,
    suggestions: data?.getAddressSuggestions?.suggestions ?? [],
    loading,
    error,
    data
  }
}
